import React, { useState } from "react";
import { Box, useMediaQuery, useTheme, Drawer, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslate } from "../../Translate/TranslateContext";

export default function ManualSidebarNav({
    sections = [],
    activeSection,
    onJump,
}) {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleItemClick = (id) => {
        onJump(id);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const navItems = (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr",
                gap: 1,
                px: isMobile ? 2 : 0,
                pb: isMobile ? 1 : 0,
                pt: isMobile ? 2 : 0,
                width: isMobile ? 250 : "100%",
            }}
        >
            {sections.map((section) => {
                const active = activeSection === section.id;

                return (
                    <Box
                        key={section.id}
                        onClick={() => handleItemClick(section.id)}
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 2,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minHeight: 48,
                            fontWeight: active ? 700 : 500,
                            bgcolor: active
                                ? "var(--color-green-soft)"
                                : "transparent",
                            color: active
                                ? "var(--color-green-strong)"
                                : "var(--color-text-primary)",
                            border: active
                                ? "1px solid var(--color-green-light)"
                                : "1px solid transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                bgcolor: "var(--color-green-soft)",
                                transform: isMobile
                                    ? "none"
                                    : "translateX(2px)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                opacity: active ? 1 : 0.75,
                                flexShrink: 0,
                            }}
                        >
                            {section.icon}
                        </Box>

                        <Box
                            sx={{
                                lineHeight: 1.2,
                                fontSize: "0.9rem",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {section.label}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );

    if (isMobile) {
        return (
            <>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        right: 16,
                        zIndex: theme.zIndex.speedDial,
                        bgcolor: "var(--color-green-soft)",
                        color: "var(--color-green-strong)",
                        border: "1px solid var(--color-green-light)",
                        "&:hover": {
                            bgcolor: "var(--color-green-light)",
                        },
                        boxShadow: 3,
                        p: 1.5,
                    }}
                >
                    <MenuIcon />
                </IconButton>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", md: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: 250,
                            bgcolor: "background.default",
                        },
                    }}
                >
                    {navItems}
                </Drawer>
            </>
        );
    }

    return (
        <Box
            sx={{
                position: "sticky",
                top: 24,
                width: "100%",
                maxWidth: 240,
                flexShrink: 0,
                alignSelf: "flex-start",
                height: "fit-content",
                bgcolor: "background.default",
                zIndex: 10,
            }}
        >
            {navItems}
        </Box>
    );
}